import React from 'react';
import { Info } from 'lucide-react';
import { Portal } from '@radix-ui/react-tooltip';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '../ui/tooltip';

const SERVICE_NAMES = {
  webcheckin: 'Web check-in',
  flightAlertsStatus: 'Flight status',
  flightAlertsSubscription: 'Flight alert subscriptions',
};

export default function PartnerUsageTooltip({ partner }) {
  const services = partner?.services;
  const codes = [
    ...new Set([
      ...Object.keys(SERVICE_NAMES),
      ...(services || []).map((service) => service.serviceCode),
    ]),
  ];
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Service usage for ${partner?.name || 'partner'}`}
            className="inline-flex items-center rounded p-1 text-gray-400 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <Info className="h-4 w-4" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <Portal>
          <TooltipContent
            side="right"
            className="max-w-[calc(100vw-2rem)] bg-white p-4 text-gray-900 shadow-lg font-normal"
          >
            <p className="font-semibold mb-1">
              Service usage · {partner?.name}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Shared across this partner’s users.
            </p>
            {!Array.isArray(services) ? (
              <p className="text-sm text-gray-500">
                Usage data is unavailable.
              </p>
            ) : (
              <>
                <table className="text-xs w-full">
                  <thead>
                    <tr>
                      {['Service', 'Allowance', 'Used', 'Remaining'].map(
                        (label) => (
                          <th
                            scope="col"
                            key={label}
                            className={`pb-2 font-medium ${label === 'Service' ? 'text-left' : 'text-right pl-4'}`}
                          >
                            {label}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((code) => {
                      const service = services.find(
                        (item) => item.serviceCode === code,
                      );
                      const blocked =
                        service &&
                        (service.status !== 'ACTIVE' ||
                          partner?.status !== 'ACTIVE');
                      return (
                        <tr key={code} className="border-t">
                          <td className="py-2 pr-2">
                            {SERVICE_NAMES[code] || code}
                            {!service && (
                              <div className="text-gray-500 mt-1">
                                Not configured
                              </div>
                            )}
                            {blocked && (
                              <div className="text-amber-700 mt-1">
                                {service.status === 'SUSPENDED'
                                  ? 'Suspended'
                                  : partner?.status !== 'ACTIVE'
                                    ? 'Partner inactive'
                                    : service.status}{' '}
                                · unavailable
                              </div>
                            )}
                          </td>
                          {[
                            'includedUnits',
                            'consumedUnits',
                            'remainingUnits',
                          ].map((field) => (
                            <td
                              key={field}
                              className="text-right pl-4 tabular-nums"
                            >
                              {service
                                ? Number(service[field]).toLocaleString()
                                : '—'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-2">
                  Remaining is unused allowance. Suspended services are
                  unavailable.
                </p>
              </>
            )}
          </TooltipContent>
        </Portal>
      </Tooltip>
    </TooltipProvider>
  );
}
